import type { CoAgentAnalyticsRow } from 'src/services/coAgentAnalyticsService';

// A chart point: the date plus one value per metric (team-wide) or per crm user id.
export type AgentChartRow = Record<string, string | number>;

/**
 * Prepares the agent analytics rows for a chart. Per-user rows become one value per user for
 * `dataKey`; team-wide rows are used as they are, or dropped when none has a `dataKey` value.
 */
export const buildAgentChartData = (
  data: CoAgentAnalyticsRow[],
  dataKey: string,
  isByUser: boolean,
): { chartData: AgentChartRow[]; userNames: string[] } => {
  const userNames: string[] = [];

  if (isByUser) {
    const chartData = data.map((dataItem) =>
      Object.keys(dataItem).reduce<AgentChartRow>((previousValue, currentValue) => {
        if (currentValue === 'date') {
          return {
            ...previousValue,
            [currentValue]: dataItem[currentValue],
          };
        }

        if (!userNames.includes(currentValue)) {
          userNames.push(currentValue);
        }

        // Every key but the date holds a user's values by metric.
        const userValues = dataItem[currentValue] as Record<string, number>;

        return {
          ...previousValue,
          [currentValue]: userValues[dataKey] || 0,
        };
      }, {}),
    );

    return { chartData, userNames };
  }

  const isEmpty = data.filter((item) => Boolean(item[dataKey])).length === 0;

  return { chartData: isEmpty ? [] : (data as AgentChartRow[]), userNames };
};
